import ApiService from '@/services/apiService';
import get from 'lodash/get';

const DEFAULT_PAGE_SIZE = 50;

export default {
  computed: {
    searchConfig() {
      const accountId = this.accountId || this.selectedAccount;
      return {
        categories: {
          quickSearch: {
            type: 'text',
            field: 'quickSearch',
          },
          title: {
            type: 'text',
            field: 'title',
          },
          group: {
            type: 'select',
            field: 'group',
            fetchOptions: this.fetchGroups,
            searchLocally: true,
          },
          tag: {
            type: 'select',
            field: 'tag',
            fetchOptions: this.fetchTags,
            searchLocally: true,
          },
          ddrTarget: {
            type: 'keyValue',
            field: 'targetRefId',
            fetchKeyOptions: this.fetchDdrSettings,
            fetchValueOptions: this.fetchDdrValues,
          },
          keyValue: {
            type: 'keyValue',
            field: 'keyValue',
          },
          'bridge.type': {
            type: 'select',
            field: 'bridge.type',
            fetchOptions: this.fetchConnectorTypes,
          },
          deviceDriver: {
            type: 'select',
            field: 'deviceDriver',
            fetchOptions: (opts) => this.fetchDrivers({ ...opts, accountId }),
          },
          deviceTemplate: {
            type: 'select',
            field: 'deviceTemplate',
            fetchOptions: (opts) => this.fetchDeviceTemplates({ ...opts, accountId }),
          },
          deviceId: {
            type: 'text',
            field: 'deviceId',
          },
          id: {
            type: 'text',
            field: 'id',
          },
          parentId: {
            type: 'dtwin',
            field: 'parentId',
          },
          operationalStatus: {
            type: 'select',
            field: 'operationalStatus',
            options: [
              { value: 0, text: this.$i18n_t('Core.MultiSearch.options.operationalStatus.0') },
              { value: 1, text: this.$i18n_t('Core.MultiSearch.options.operationalStatus.1') },
              { value: 2, text: this.$i18n_t('Core.MultiSearch.options.operationalStatus.2') },
              { value: 3, text: this.$i18n_t('Core.MultiSearch.options.operationalStatus.3') },
              { value: 4, text: this.$i18n_t('Core.MultiSearch.options.operationalStatus.4') },
              { value: 5, text: this.$i18n_t('Core.MultiSearch.options.operationalStatus.5') },
            ],
          },
          health: {
            type: 'select',
            field: 'health',
            options: [
              { value: 0, text: this.$i18n_t('Core.MultiSearch.options.health.0') },
              { value: 1, text: this.$i18n_t('Core.MultiSearch.options.health.1') },
              { value: 2, text: this.$i18n_t('Core.MultiSearch.options.health.2') },
              { value: 3, text: this.$i18n_t('Core.MultiSearch.options.health.3') },
              { value: 4, text: this.$i18n_t('Core.MultiSearch.options.health.4') },
            ],
          },
          address: {
            type: 'text',
            field: 'address',
          },
        },
      };
    },
  },
  watch: {
    accountId() {
      this.fetchDrivers({ page: 1, pageSize: 10, accountId: this.accountId });
      this.fetchDeviceTemplates({ page: 0, pageSize: 10, accountId: this.accountId });
    },
    selectedAccount() {
      this.fetchDrivers({ page: 1, pageSize: 10, accountId: this.selectedAccount });
      this.fetchDeviceTemplates({ page: 0, pageSize: 10, accountId: this.selectedAccount });
    },
  },
  methods: {
    async fetchGroups() {
      const response = await ApiService.virtualDeviceFetchGroups(this, null);
      return response.groupList.map((group) => {
        return { value: group, text: group };
      });
    },
    async fetchTags() {
      const response = await ApiService.virtualDeviceFetchTags();
      return response.tagList.map((tag) => {
        return { value: tag, text: tag };
      });
    },
    async fetchDdrSettings(options = {}) {
      const { offset = 0, limit = DEFAULT_PAGE_SIZE, search = '' } = options || {};
      const response = await ApiService.fetchDdrSettings({
        offset,
        limit,
        search,
      });

      return {
        items: response.entities.map((entity) => {
          return { value: entity, text: entity };
        }),
        totalCount: response.totalCount,
      };
    },
    async fetchDdrValues(selectedKey, options = {}) {
      const { offset = 0, limit = DEFAULT_PAGE_SIZE, search = '' } = options || {};
      const response = await ApiService.fetchDdrSettings({
        offset,
        limit,
        entityType: 'values',
        key: selectedKey,
        search,
      });

      return response.entities.map((entity) => {
        return { value: entity, text: entity };
      });
    },
    async fetchDrivers({ page, pageSize, search, accountId }) {
      const selectedAccountId = accountId ? accountId : this.accountId ? this.accountId : null;
      const response = await ApiService.virtualDeviceDrivers.fetch({
        fields: 'id,title',
        accountIds: selectedAccountId ? [selectedAccountId] : (this.selectedAccounts || []).map((acc) => acc.id),
        offset: (page - 1) * pageSize,
        limit: pageSize,
        search,
        sortBy: 'title',
      });
      return response.deviceDrivers.map((dt) => {
        return { value: dt.id, text: dt.title };
      });
    },
    async fetchDeviceTemplates({ page, pageSize, search, accountId }) {
      const selectedAccountId = accountId ? accountId : this.accountId ? this.accountId : null;
      const searchItems = search ? [{ variable: 'title', search }] : [];
      const response = await ApiService.deviceTemplatesFetch({
        tenantIds: selectedAccountId ? [selectedAccountId] : (this.selectedAccounts || []).map((acc) => acc.id),
        page,
        pageSize,
        searchItems,
      });

      return response.data.map((dt) => {
        return { value: dt.ProductiveVersion.id, text: dt.ProductiveVersion.title };
      });
    },
    async fetchConnectorTypes() {
      const response = await ApiService.getBridgeTypes();
      const types = response
        .filter((bridge) => {
          if (
            get(bridge, 'manifest.functionScope.extension.devices.create') ||
            get(bridge, 'manifest.functionScope.extension.devices.read')
          ) {
            return true;
          }
          return false;
        })
        .map((bridge) => bridge.manifest.displayName);

      return types.map((type) => {
        return { value: type, text: type };
      });
    },
  },
};
